/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\HotDeskingQuickListModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/


import { Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { QuickSearchModal } from '../../../components/ui/QuickSearchModal';
import { useI18n } from '../../../hooks/useI18n';
import { getSettingValue } from '../utils/settingsHelpers';
import type { HotDeskDevice } from '../types';

const LIST_PATH = '/pbx/extensions/hot-desking';

interface Props {
    isOpen: boolean;
    currentItemId?: string;
    devices: HotDeskDevice[];
    isLoadingInitial: boolean;
    isSearching: boolean;
    searchValue: string;
    onSearchChange: (v: string) => void;
    onClose: () => void;
}

export function HotDeskingQuickListModal({
    isOpen, currentItemId, devices, isLoadingInitial, isSearching,
    searchValue, onSearchChange, onClose,
}: Props) {
    const { t } = useI18n();
    const navigate = useNavigate();

    const handleSelect = (device: HotDeskDevice) => {
        onClose();
        navigate(`${LIST_PATH}/${device.uuid || device.id}`);
    };

    return (
        <QuickSearchModal
            isOpen={isOpen}
            onClose={onClose}
            title={t('hotDesking.actions.quickList')}
            items={devices}
            isLoadingInitial={isLoadingInitial}
            isSearching={isSearching}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onItemSelect={handleSelect}
            currentItemId={currentItemId}
            renderItem={(device, isCurrent) => {
                const description =
                    (typeof device.settings?.description === 'string' ? device.settings.description : '') ||
                    getSettingValue(device.settings, 'description') || '';
                return (
                    <button
                        onClick={() => { if (!isCurrent) handleSelect(device); }}
                        disabled={isCurrent}
                        className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
                            ? 'bg-primary/20 border-2 border-primary cursor-default'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'}`}
                    >
                        <div className="flex items-center gap-3">
                            <Phone className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-foreground">{device.username}</span>
                                    {isCurrent && (
                                        <span className="text-xs px-2 py-0.5 bg-primary text-primary-foreground rounded">
                                            {t('extensions.current')}
                                        </span>
                                    )}
                                </div>
                                {description && <p className="text-sm text-muted-foreground truncate">{description}</p>}
                            </div>
                        </div>
                    </button>
                );
            }}
            searchPlaceholder={searchValue.length === 0 ? t('hotDesking.quickListSearch') : t('hotDesking.quickListSearchWithMin', 'Type at least 2 characters to search...')}
            minCharsMessage={t('hotDesking.quickListMinChars')}
            emptyMessage={t('hotDesking.quickListPrompt')}
            noResultsMessage={t('hotDesking.quickListNoResults')}
            headerIcon={<Phone className="h-5 w-5" />}
            minSearchChars={2}
        />
    );
}
